import api, { setTokens, clearTokens } from './api';
import type { User, LoginCredentials, RegisterData, AuthTokens } from '../types';

interface RegisterResponse {
  email: string;
  message: string;
}

interface LoginResponse {
  access: string;
  refresh: string;
}

export const authService = {
  async register(data: RegisterData): Promise<RegisterResponse> {
    const response = await api.post<RegisterResponse>('/auth/register/', data);
    return response.data;
  },

  async resendVerificationByEmail(email: string): Promise<void> {
    await api.post('/auth/resend-verification-by-email/', { email });
  },

  async login(credentials: LoginCredentials): Promise<User> {
    const response = await api.post<LoginResponse>('/auth/token/', credentials);
    setTokens({ access: response.data.access, refresh: response.data.refresh });
    const user = await authService.getMe();
    return user;
  },

  async logout(): Promise<void> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      try {
        await api.post('/auth/logout/', { refresh: refreshToken });
      } catch {
        // Ignore errors — we're logging out anyway
      }
    }
    clearTokens();
  },

  async getMe(): Promise<User> {
    const response = await api.get<User>('/auth/me/');
    return response.data;
  },

  async updateMe(data: Partial<Pick<User, 'first_name' | 'last_name' | 'phone'>>): Promise<User> {
    const response = await api.patch<User>('/auth/me/', data);
    return response.data;
  },

  /** Feature 12 — mark the user as having finished the first-login wizard. */
  async completeSetup(): Promise<{ setup_completed_at: string }> {
    const response = await api.post<{ setup_completed_at: string }>(
      '/auth/setup-complete/',
    );
    return response.data;
  },

  async verifyEmail(token: string): Promise<{ email?: string; detail?: string }> {
    const response = await api.post<{ email?: string; detail?: string }>(
      '/auth/verify-email/',
      { token }
    );
    return response.data;
  },

  async resendVerification(): Promise<void> {
    await api.post('/auth/resend-verification/');
  },

  async forgotPassword(email: string): Promise<void> {
    await api.post('/auth/forgot-password/', { email });
  },

  async resetPassword(token: string, new_password: string): Promise<void> {
    await api.post('/auth/reset-password/', { token, new_password });
  },

  async changePassword(old_password: string, new_password: string): Promise<AuthTokens> {
    const response = await api.post<{ detail: string; tokens: AuthTokens }>(
      '/auth/change-password/',
      { old_password, new_password }
    );
    setTokens(response.data.tokens);
    return response.data.tokens;
  },
};
